import {
  BlotterTable,
  Box,
  FormControlSizes,
  getIncrementFromScale,
  HStack,
  Icon,
  IconButton,
  IconName,
  IndicatorDotVariants,
  IndicatorDotWrapper,
  InlineFormattedNumber,
  isPricingModeRelative,
  LoaderSizes,
  LoaderTalos,
  NumberInput,
  SideEnum,
  Text,
  Tooltip,
  useBlotterObservables,
  useBlotterTable,
  type ColumnDef,
  type Currency,
  type NumericField,
  type PriceParams,
  type PricingModeEnum,
  type SizeColumnParams,
} from '@talos/kyoko';
import type { ColDef, ICellRendererParams, ValueGetterParams } from 'ag-grid-community';
import { useMemo } from 'react';

import {
  RFQSweeperFillExecutionStatus,
  type RFQSweeper,
  type RFQSweeperFill,
  type RFQSweeperFillExecutionState,
} from '../RFQSweeper';

interface SweeperBlotterProps {
  sweeper: RFQSweeper;
  symbol: string | undefined;
  pricingMode: PricingModeEnum | undefined;
  showAllDecimals: boolean;
  onDesiredQtyChange: (marketName: string, value: string) => void;
  onRemoveMarket: (marketName: string) => void;
}

interface SweeperRow {
  rowID: string;
  Market: string;
  Side: SideEnum;
  Currency: string | undefined;
  Symbol: string | undefined;
  PricingMode: PricingModeEnum | undefined;
  availableQty: string;
  desiredQty: NumericField;
  executionStatus: RFQSweeperFillExecutionState;
  quotePx: string;
  currency: Currency | undefined;
  canPartiallyFillAtMarkets: boolean;
}

interface DesiredQtyColumnValue {
  marketName: string;
  field: NumericField;
  currency: Currency | undefined;
  executionStatus: RFQSweeperFillExecutionState;
  canPartiallyFillAtMarkets: boolean;
}

interface SweeperActionColumnValue {
  marketName: string;
  executionStatus: RFQSweeperFillExecutionState;
}

export function SweeperBlotter({
  sweeper,
  symbol,
  pricingMode,
  showAllDecimals,
  onDesiredQtyChange,
  onRemoveMarket,
}: SweeperBlotterProps) {
  const rows = useMemo(
    () =>
      sweeper.fills.map(fill =>
        newSweeperRow(fill, sweeper.side, sweeper.currency, symbol, pricingMode, sweeper.canPartiallyFillAtMarkets)
      ),
    [pricingMode, sweeper.canPartiallyFillAtMarkets, sweeper.currency, sweeper.fills, sweeper.side, symbol]
  );
  const columns = useSweeperColumns(sweeper.side, onDesiredQtyChange, onRemoveMarket);
  const { dataObservable, errorObservable } = useBlotterObservables({ data: rows });
  const blotterSettings = useMemo(
    () => ({
      showAllDecimals,
    }),
    [showAllDecimals]
  );

  const blotterTable = useBlotterTable<SweeperRow>({
    dataObservable,
    errorObservable,
    columns,
    rowID: 'rowID',
    settings: blotterSettings,
  });

  return <BlotterTable {...blotterTable} />;
}

function useSweeperColumns(
  side: SideEnum,
  onDesiredQtyChange: SweeperBlotterProps['onDesiredQtyChange'],
  onRemoveMarket: SweeperBlotterProps['onRemoveMarket']
): ColumnDef<SweeperRow>[] {
  return useMemo<ColumnDef<SweeperRow>[]>(
    () => [
      getSweeperActionColumn(onRemoveMarket),

      {
        type: 'market',
        field: 'Market',
        title: 'Market',
        initialWidth: 150,
      },
      {
        type: 'side',
        field: 'Side',
        title: 'Side',
        initialWidth: 50,
      },
      {
        type: 'size',
        field: 'availableQty',
        title: 'Quoted Qty',
        initialWidth: 160,
        params: {
          currencyField: 'Currency',
        } satisfies SizeColumnParams<SweeperRow>,
      },
      getDesiredQtyColumn(onDesiredQtyChange),
      {
        type: 'price',
        field: 'quotePx',
        title: 'Quote',
        initialWidth: 100,
        // Keep this controlled so flipping side re-sorts the basket: buys consume low offers, sells consume high bids.
        sort: side === SideEnum.Buy ? '+' : '-',
        params: {
          assetField: 'Symbol',
          isReferencePrice: data => isPricingModeRelative(data?.PricingMode),
          increment: data => (isPricingModeRelative(data?.PricingMode) ? '0.00' : undefined),
        } satisfies PriceParams<SweeperRow>,
      },
      getExecutionStatusColumn(),
    ],
    [onDesiredQtyChange, onRemoveMarket, side]
  );
}

function getSweeperActionColumn(onRemoveMarket: SweeperBlotterProps['onRemoveMarket']): ColumnDef<SweeperRow> {
  return {
    type: 'custom',
    id: 'sweeperAction',
    title: '',
    width: 36,
    pinned: 'right',
    sortable: false,
    params: {
      suppressKeyboardEvent: () => true,
      valueGetter: (params: ValueGetterParams<SweeperRow>): SweeperActionColumnValue | undefined => {
        if (!params.data) {
          return undefined;
        }

        return {
          marketName: params.data.Market,
          executionStatus: params.data.executionStatus,
        };
      },
      cellRenderer: ({ value }: ICellRendererParams<SweeperRow, SweeperActionColumnValue | undefined>) => {
        if (!value) {
          return <></>;
        }

        return (
          <IconButton
            icon={IconName.Close}
            size={FormControlSizes.Tiny}
            ghost
            disabled={value.executionStatus.status === RFQSweeperFillExecutionStatus.Filled}
            onClick={() => onRemoveMarket(value.marketName)}
            data-testid={`sweeper-remove-${value.marketName}`}
          />
        );
      },
    } satisfies ColDef<SweeperRow>,
  };
}

function getExecutionStatusColumn(): ColumnDef<SweeperRow> {
  return {
    type: 'custom',
    id: 'executionStatus',
    title: 'Fill Status',
    width: 90,
    sortable: false,
    params: {
      suppressKeyboardEvent: () => true,
      valueGetter: (params: ValueGetterParams<SweeperRow>) => params.data?.executionStatus,
      cellRenderer: ({ data, value }: ICellRendererParams<SweeperRow, RFQSweeperFillExecutionState | undefined>) => {
        if (!value || value.status === RFQSweeperFillExecutionStatus.Staged) {
          return <></>;
        }

        if (value.status === RFQSweeperFillExecutionStatus.Pending) {
          return (
            <Box>
              <LoaderTalos size={LoaderSizes.XS} />
            </Box>
          );
        }

        if (value.status === RFQSweeperFillExecutionStatus.Filled) {
          return (
            <ExecutionStatusCell
              icon={IconName.CheckCircleSolid}
              color="colors.green.lighten"
              label="Filled"
              marketName={data?.Market}
            />
          );
        }

        return (
          <Tooltip tooltip={value.tooltip} disabled={!value.tooltip}>
            <ExecutionStatusCell
              icon={IconName.ExclamationCircleSolid}
              color="colors.yellow.lighten"
              label="Rejected"
              marketName={data?.Market}
              showTooltipDot={!!value.tooltip}
            />
          </Tooltip>
        );
      },
    } satisfies ColDef<SweeperRow>,
  };
}

function ExecutionStatusCell({
  icon,
  color,
  label,
  marketName,
  showTooltipDot = false,
}: {
  icon: IconName;
  color: string;
  label: string;
  marketName: string | undefined;
  showTooltipDot?: boolean;
}) {
  return (
    <HStack gap="spacing4" alignItems="center" data-testid={`sweeper-execution-status-${marketName}`}>
      <IndicatorDotWrapper show={showTooltipDot} variant={IndicatorDotVariants.Warning}>
        <Icon icon={icon} color={color} size="fontSizeLarge" />
      </IndicatorDotWrapper>
      <Text color="colorTextAttention" fontSize="fontSizeSm">
        {label}
      </Text>
    </HStack>
  );
}

function getDesiredQtyColumn(onDesiredQtyChange: SweeperBlotterProps['onDesiredQtyChange']): ColumnDef<SweeperRow> {
  return {
    type: 'custom',
    id: 'desiredQty',
    title: 'Desired Qty',
    initialWidth: 180,
    sortable: false,
    headerClass: 'ag-right-aligned-cell',
    params: {
      suppressKeyboardEvent: () => true,
      valueGetter: (params: ValueGetterParams<SweeperRow>): DesiredQtyColumnValue | undefined => {
        if (!params.data) {
          return undefined;
        }

        return {
          marketName: params.data.Market,
          field: params.data.desiredQty,
          currency: params.data.currency,
          executionStatus: params.data.executionStatus,
          canPartiallyFillAtMarkets: params.data.canPartiallyFillAtMarkets,
        };
      },
      cellRenderer: ({ value }: ICellRendererParams<SweeperRow, DesiredQtyColumnValue>) => {
        if (!value) {
          return <></>;
        }

        const { marketName, field, currency, executionStatus, canPartiallyFillAtMarkets } = value;

        if (executionStatus.status === RFQSweeperFillExecutionStatus.Filled || !canPartiallyFillAtMarkets) {
          return (
            <HStack w="100%" justifyContent="flex-end" data-testid={`sweeper-desired-qty-static-${marketName}`}>
              <InlineFormattedNumber
                number={field.value}
                increment={currency?.DefaultIncrement}
                currency={currency?.Symbol ?? 'UNITS'}
                align="right"
              />
            </HStack>
          );
        }

        return (
          <NumberInput
            width="100%"
            value={field.displayValue}
            onChange={nextValue => onDesiredQtyChange(marketName, nextValue)}
            minIncrement={getIncrementFromScale(field.scale)}
            defaultIncrement={currency?.DefaultIncrement ?? '1'}
            suffix={currency?.Symbol ?? 'UNITS'}
            invalid={field.hasError}
            disabled={field.isDisabled}
            size={FormControlSizes.Small}
            prefix={
              field.errorString ? (
                <Tooltip tooltip={field.errorString}>
                  <Icon
                    icon={IconName.ExclamationSolid}
                    color="colors.red.lighten"
                    data-testid={`sweeper-desired-qty-error-${marketName}`}
                  />
                </Tooltip>
              ) : undefined
            }
            data-testid={`sweeper-desired-qty-${marketName}`}
            autoFocus
          />
        );
      },
    } satisfies ColDef<SweeperRow>,
  };
}

function newSweeperRow(
  fill: RFQSweeperFill,
  side: SideEnum,
  currency: Currency | undefined,
  symbol: string | undefined,
  pricingMode: PricingModeEnum | undefined,
  canPartiallyFillAtMarkets: boolean
): SweeperRow {
  return {
    rowID: fill.quote.quoteMarket.Market,
    Market: fill.quote.quoteMarket.Market,
    Side: side,
    Currency: currency?.Symbol,
    Symbol: symbol,
    PricingMode: pricingMode,
    availableQty: fill.availableQty.toFixed(),
    desiredQty: fill.desiredQty,
    executionStatus: fill.executionStatus,
    quotePx: fill.px,
    currency,
    canPartiallyFillAtMarkets,
  };
}
